// src/pages/Contracts.tsx
// CM-01: Two-tab Contract Management — Project Contracts (AS2124/AS4000/NEC4/SOA) + Supplier Compliance
// A13 CA Agent sidebar integrated on Project Contracts tab
// Gap status: ABSENT (project contracts) → BUILT

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import {
  MOCK_CONTRACTS as MOCK_SUPPLIER_CONTRACTS,
  CONTRACT_STATUS_META, CONTRACT_TYPE_META, DOC_TYPE_META,
  SupplierContract, ComplianceDocument, ContractStatus,
  daysUntilExpiry, docStatus, contractStatus, getExpiringDocuments,
} from '../utils/mockDataContracts'
import { AgentSidebar } from '../components/AgentSidebar'
import {
  MOCK_CONTRACTS as MOCK_PROJECT_CONTRACTS,
  MOCK_VARIATIONS, MOCK_EOT_CLAIMS, MOCK_PROGRESS_CLAIMS,
  getVariationNoticeDaysLeft, getNoticeUrgency,
} from '../utils/mockDataCommercial'
import { MOCK_JOBS } from '../utils/mockData'
import { getCreatedContracts } from '../utils/mockDataProjectGaps'
import {
  FileText, Plus, Search, AlertTriangle, CheckCircle, Clock,
  ChevronRight, X, Download, Upload, Building, Calendar,
  DollarSign, Shield, ExternalLink, Pencil,
} from 'lucide-react'

// ── Formatters ────────────────────────────────────────────────────
const fmtAUD  = (n: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 }).format(n)
const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'

// ── Contract type metadata (for project contracts) ────────────────
const PROJ_TYPE_COLOR: Record<string, string> = {
  AS2124:    'bg-blue-50 text-blue-700',
  AS4000:    'bg-indigo-50 text-indigo-700',
  NEC4:      'bg-purple-50 text-purple-700',
  PANEL_SOA: 'bg-teal-50 text-teal-700',
}
const PROJ_TYPE_NAME: Record<string, string> = {
  AS2124: 'AS2124', AS4000: 'AS4000', NEC4: 'NEC4', PANEL_SOA: 'SOA Panel',
}
const TIME_BAR: Record<string, string> = {
  AS2124: 'AS2124 Cl 40.1 — 28-day time bar',
  AS4000: 'AS4000 Cl 41.1 — 14-day time bar',
  NEC4:   'NEC4 Cl 61.3 — 8-week notice required',
}

// ══════════════════════════════════════════════════════════════════
//  SUPPLIER COMPLIANCE components (unchanged from original)
// ══════════════════════════════════════════════════════════════════

function ExpiryBadge({ expiryDate, alertDays }: { expiryDate: string; alertDays: number }) {
  const days = daysUntilExpiry(expiryDate)
  if (days < 0)          return <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">Expired {Math.abs(days)}d ago</span>
  if (days <= 30)        return <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">Expires in {days}d</span>
  if (days <= alertDays) return <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Expires {fmtDate(expiryDate)}</span>
  return <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Valid · {fmtDate(expiryDate)}</span>
}

function DocumentRow({ doc, canEdit }: { doc: ComplianceDocument; canEdit: boolean }) {
  const st       = docStatus(doc)
  const rowBg    = st === 'EXPIRED' ? 'bg-red-50' : st === 'EXPIRING' ? 'bg-amber-50/60' : ''
  const typeMeta = DOC_TYPE_META[doc.type]
  return (
    <tr className={`border-b border-gray-50 hover:bg-gray-50 ${rowBg}`}>
      <td className="px-4 py-2.5">
        <div className="text-xs font-medium text-gray-900">{typeMeta.label}</div>
        {doc.policyOrRefNumber && <div className="text-[10px] text-gray-400 font-mono">{doc.policyOrRefNumber}</div>}
      </td>
      <td className="px-3 py-2.5 text-xs text-gray-600">{doc.issuer}</td>
      {doc.coverAmount != null
        ? <td className="px-3 py-2.5 text-xs text-gray-700 tabular-nums">{fmtAUD(doc.coverAmount)}</td>
        : <td className="px-3 py-2.5 text-xs text-gray-400">—</td>}
      <td className="px-3 py-2.5 text-xs text-gray-500">{fmtDate(doc.issueDate)}</td>
      <td className="px-3 py-2.5"><ExpiryBadge expiryDate={doc.expiryDate} alertDays={doc.alertDaysBefore}/></td>
      <td className="px-3 py-2.5">
        {doc.isUploaded
          ? <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle className="w-3 h-3"/>Uploaded</span>
          : <span className="text-xs text-red-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3"/>Not uploaded</span>}
      </td>
      <td className="px-4 py-2.5 text-right">
        {doc.isUploaded && <button className="p-1 text-gray-400 hover:text-gray-600"><Download className="w-3.5 h-3.5"/></button>}
        {canEdit && (
          <button className="text-xs text-[#2E75B6] hover:text-[#1F3864] px-2 py-0.5 rounded border border-[#2E75B6]/30 flex items-center gap-1">
            <Upload className="w-3 h-3"/>{doc.isUploaded ? 'Update' : 'Upload'}
          </button>
        )}
      </td>
    </tr>
  )
}

function ContractDetailPanel({ contract, onClose, canEdit }: { contract: SupplierContract; onClose: () => void; canEdit: boolean }) {
  const [tab, setTab] = useState<'overview' | 'documents' | 'pricing'>('overview')
  const cs           = contractStatus(contract)
  const csMeta       = CONTRACT_STATUS_META[cs]
  const ctMeta       = CONTRACT_TYPE_META[contract.contractType]
  const expiringDocs = contract.documents.filter(d => docStatus(d) !== 'VALID')
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-4 border-b bg-gray-50 flex-shrink-0">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-sm">{ctMeta.icon}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${csMeta.bg} ${csMeta.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${csMeta.dot}`}/>{csMeta.label}
              </span>
              {expiringDocs.length > 0 && (
                <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium">
                  ⚑ {expiringDocs.length} doc{expiringDocs.length !== 1 ? 's' : ''} expiring
                </span>
              )}
            </div>
            <div className="text-sm font-bold text-gray-900">{contract.title}</div>
            <div className="text-xs text-gray-500 mt-0.5">{contract.supplierName} · {contract.contractNumber}</div>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:bg-gray-200 rounded-lg"><X className="w-4 h-4"/></button>
        </div>
        <div className="flex gap-1 mt-3">
          {(['overview', 'documents', 'pricing'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`text-xs px-3 py-1.5 rounded-lg font-medium capitalize transition-colors ${tab === t ? 'bg-[#1F3864] text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
              {t}
              {t === 'documents' && expiringDocs.length > 0 && (
                <span className="ml-1.5 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">{expiringDocs.length}</span>
              )}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {tab === 'overview' && (
          <>
            <div className="bg-[#1F3864] rounded-xl p-4 text-white">
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['Supplier', contract.supplierName],
                  ['ABN', contract.supplierABN],
                  ['Contract type', ctMeta.label],
                  ['Payment terms', `${contract.paymentTermsDays} days`],
                  ['Start date', fmtDate(contract.startDate)],
                  ['End date', contract.endDate ? fmtDate(contract.endDate) : 'Ongoing'],
                  ...(contract.totalValue  ? [['Total value',  fmtAUD(contract.totalValue)]]  : []),
                  ...(contract.annualValue ? [['Annual value', fmtAUD(contract.annualValue)]] : []),
                ].map(([label, value]) => (
                  <div key={String(label)}>
                    <div className="text-[10px] text-blue-300 uppercase tracking-wide">{label}</div>
                    <div className="text-sm font-semibold">{value}</div>
                  </div>
                ))}
              </div>
            </div>
            {contract.renewalNoticeDate && daysUntilExpiry(contract.renewalNoticeDate) <= 90 && (
              <div className={`rounded-xl border px-3 py-2.5 flex items-start gap-2 text-xs ${daysUntilExpiry(contract.renewalNoticeDate) < 0 ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"/>
                <div>
                  <div className="font-semibold">Renewal notice due {fmtDate(contract.renewalNoticeDate)}</div>
                  <div className="mt-0.5">Initiate renewal with {contract.supplierContact} before this date.</div>
                </div>
              </div>
            )}
            <p className="text-sm text-gray-700 leading-relaxed">{contract.description}</p>
            {contract.notes && (
              <div className={`rounded-xl border px-3 py-2.5 text-xs ${contract.notes.startsWith('⚠') ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                {contract.notes}
              </div>
            )}
            <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-900">{contract.supplierContact}</div>
                <div className="text-xs text-gray-500">{contract.supplierEmail}</div>
              </div>
              <a href={`mailto:${contract.supplierEmail}`} className="text-xs text-[#2E75B6] hover:underline flex items-center gap-1">
                <ExternalLink className="w-3 h-3"/>Email
              </a>
            </div>
          </>
        )}
        {tab === 'documents' && (
          <div className="space-y-3">
            {expiringDocs.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-xs text-red-800">
                <div className="font-semibold mb-1 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5"/>{expiringDocs.length} document{expiringDocs.length !== 1 ? 's' : ''} expired or expiring</div>
                {expiringDocs.map(d => <div key={d.id} className="text-[10px]">• {DOC_TYPE_META[d.type].label}: expires {fmtDate(d.expiryDate)}</div>)}
              </div>
            )}
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead><tr className="bg-gray-50 border-b">
                  {['Document','Issuer','Cover','Issued','Expiry','File',''].map(h => <th key={h} className="text-left px-4 py-2.5 font-medium text-gray-600">{h}</th>)}
                </tr></thead>
                <tbody>{contract.documents.map(doc => <DocumentRow key={doc.id} doc={doc} canEdit={canEdit}/>)}</tbody>
              </table>
            </div>
            {canEdit && <button className="w-full btn-secondary flex items-center justify-center gap-2 text-xs"><Plus className="w-3.5 h-3.5"/>Add compliance document</button>}
          </div>
        )}
        {tab === 'pricing' && (
          <div className="space-y-3">
            {contract.pricingTerms.length === 0 ? (
              <div className="border border-dashed border-gray-300 rounded-xl py-12 text-center text-gray-400 text-sm">
                No agreed pricing terms
                {canEdit && <div className="mt-2"><button className="btn-secondary text-xs">+ Add pricing term</button></div>}
              </div>
            ) : (
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-50 border-b">
                    {['Description','Rate','Effective from','Notes'].map(h => <th key={h} className="text-left px-4 py-2.5 font-medium text-gray-600">{h}</th>)}
                  </tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {contract.pricingTerms.map(pt => (
                      <tr key={pt.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5 font-medium text-gray-800">{pt.itemDescription}</td>
                        <td className="px-3 py-2.5 font-mono font-semibold">{fmtAUD(pt.agreedRate)}/{pt.unit}</td>
                        <td className="px-3 py-2.5 text-gray-500">{fmtDate(pt.effectiveFrom)}</td>
                        <td className="px-4 py-2.5 text-gray-400 text-[10px] italic">{pt.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
      {canEdit && (
        <div className="border-t px-5 py-3 flex gap-2">
          <button className="btn-secondary flex items-center gap-1.5 text-xs flex-1"><Pencil className="w-3.5 h-3.5"/>Edit contract</button>
          <button className="btn-secondary flex items-center gap-1.5 text-xs"><Download className="w-3.5 h-3.5"/>Export PDF</button>
        </div>
      )}
    </div>
  )
}

// ── Supplier Compliance tab content ───────────────────────────────
function SupplierComplianceTab({ canEdit }: { canEdit: boolean }) {
  const [search, setSearch]       = useState('')
  const [statusFilter, setStatus] = useState<ContractStatus | 'ALL'>('ALL')
  const [showAdd, setShowAdd]     = useState(false)
  const [selected, setSelected]   = useState<SupplierContract | null>(null)

  const filtered = MOCK_SUPPLIER_CONTRACTS.filter(c => {
    const cs = contractStatus(c)
    if (statusFilter !== 'ALL' && cs !== statusFilter) return false
    const q = search.toLowerCase()
    return !q || c.supplierName.toLowerCase().includes(q) || c.title.toLowerCase().includes(q) || c.contractNumber.toLowerCase().includes(q)
  })
  const allExpiring = getExpiringDocuments(MOCK_SUPPLIER_CONTRACTS, 90)
  const activeCount = MOCK_SUPPLIER_CONTRACTS.filter(c => contractStatus(c) === 'ACTIVE').length
  const expiringSoonCount = MOCK_SUPPLIER_CONTRACTS.filter(c => contractStatus(c) === 'EXPIRING_SOON').length

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Active supplier contracts', value: activeCount,            color: 'text-green-700', Icon: CheckCircle },
          { label: 'Expiring <90 days',         value: expiringSoonCount,      color: expiringSoonCount > 0 ? 'text-amber-600' : 'text-gray-400', Icon: Clock },
          { label: 'Documents expiring',         value: allExpiring.length,     color: allExpiring.length > 0 ? 'text-red-600' : 'text-gray-400', Icon: Shield },
        ].map(({ label, value, color, Icon }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3.5 flex items-start justify-between">
            <div>
              <div className={`text-2xl font-bold ${color}`}>{value}</div>
              <div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div>
            </div>
            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
              <Icon className={`w-4 h-4 ${color}`}/>
            </div>
          </div>
        ))}
      </div>

      {/* Expiry banner */}
      {allExpiring.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 space-y-1.5">
          <div className="text-sm font-semibold text-red-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4"/>
            {allExpiring.length} compliance document{allExpiring.length !== 1 ? 's' : ''} expired or expiring within 90 days
          </div>
          <div className="flex flex-wrap gap-2">
            {allExpiring.slice(0, 4).map(({ contract, doc, days }) => (
              <div key={doc.id} onClick={() => setSelected(contract)}
                className={`text-xs px-2.5 py-1 rounded-lg border font-medium cursor-pointer hover:opacity-80 ${days < 0 ? 'bg-red-100 text-red-700 border-red-200' : 'bg-amber-100 text-amber-700 border-amber-200'}`}>
                {contract.supplierName.split(' ')[0]} · {DOC_TYPE_META[doc.type].short} · {days < 0 ? `${Math.abs(days)}d ago` : `${days}d`}
              </div>
            ))}
            {allExpiring.length > 4 && <div className="text-xs text-red-600 px-2 py-1">+{allExpiring.length - 4} more</div>}
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"/>
          <input className="input pl-9 text-sm" placeholder="Search supplier or contract…" value={search} onChange={e => setSearch(e.target.value)}/>
        </div>
        {(['ALL','ACTIVE','EXPIRING_SOON','EXPIRED','PENDING'] as (ContractStatus|'ALL')[]).map(f => {
          const count = f === 'ALL' ? MOCK_SUPPLIER_CONTRACTS.length : MOCK_SUPPLIER_CONTRACTS.filter(c => contractStatus(c) === f).length
          if (count === 0 && f !== 'ALL') return null
          return (
            <button key={f} onClick={() => setStatus(f)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${statusFilter === f ? 'bg-[#1F3864] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {f === 'ALL' ? 'All' : CONTRACT_STATUS_META[f as ContractStatus].label}
              <span className={`ml-1.5 text-[10px] font-bold ${statusFilter === f ? 'text-white/70' : 'text-gray-400'}`}>{count}</span>
            </button>
          )
        })}
        {canEdit && (
          <button onClick={() => setShowAdd(!showAdd)} className="btn-primary text-sm flex items-center gap-1.5"><Plus className="w-4 h-4"/>Add contract</button>
        )}
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-blue-900 text-sm">New supplier contract</h4>
            <button onClick={() => setShowAdd(false)}><X className="w-4 h-4 text-gray-400"/></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="block text-xs font-medium text-gray-700 mb-1">Contract title *</label><input className="input text-sm"/></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Supplier *</label><input className="input text-sm"/></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Contract type</label>
              <select className="input text-sm"><option>Preferred supplier agreement</option><option>Supply agreement</option><option>Subcontract</option></select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowAdd(false)} className="btn-secondary text-sm">Cancel</button>
            <button className="btn-primary text-sm">Create</button>
          </div>
        </div>
      )}

      {/* List + detail */}
      <div className="flex gap-4 items-start">
        <div className="flex-1 min-w-0 space-y-2">
          {filtered.length === 0 ? (
            <div className="bg-white border border-dashed border-gray-200 rounded-xl py-16 text-center">
              <FileText className="w-10 h-10 text-gray-200 mx-auto mb-3"/>
              <p className="font-medium text-gray-400">No contracts found</p>
            </div>
          ) : filtered.map(contract => {
            const cs     = contractStatus(contract)
            const csMeta = CONTRACT_STATUS_META[cs]
            const ctMeta = CONTRACT_TYPE_META[contract.contractType]
            const isSelected  = selected?.id === contract.id
            const expiringDocs = contract.documents.filter(d => docStatus(d) !== 'VALID')
            return (
              <div key={contract.id} onClick={() => setSelected(isSelected ? null : contract)}
                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${
                  isSelected ? 'border-blue-400 ring-2 ring-blue-100 shadow-sm' :
                  expiringDocs.length > 0 ? 'border-red-200' :
                  cs === 'EXPIRING_SOON' ? 'border-amber-300' : 'border-gray-200 hover:border-gray-300'
                }`}>
                <div className="flex items-start gap-3">
                  <div className="text-xl flex-shrink-0 mt-0.5">{ctMeta.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-mono text-gray-400">{contract.contractNumber}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${csMeta.bg} ${csMeta.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${csMeta.dot}`}/>{csMeta.label}
                      </span>
                      {expiringDocs.length > 0 && (
                        <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium">
                          ⚑ {expiringDocs.length} doc{expiringDocs.length !== 1 ? 's' : ''} expiring
                        </span>
                      )}
                    </div>
                    <div className="font-semibold text-gray-900 text-sm">{contract.title}</div>
                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1"><Building className="w-3 h-3"/>{contract.supplierName}</div>
                    <div className="flex items-center gap-3 mt-2 flex-wrap text-xs text-gray-400">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3"/>{fmtDate(contract.startDate)} — {contract.endDate ? fmtDate(contract.endDate) : 'Ongoing'}</span>
                      {contract.annualValue && <span className="flex items-center gap-1"><DollarSign className="w-3 h-3"/>{fmtAUD(contract.annualValue)}/yr</span>}
                      {contract.totalValue  && <span className="flex items-center gap-1"><DollarSign className="w-3 h-3"/>{fmtAUD(contract.totalValue)} fixed</span>}
                      <span>{contract.documents.length} docs</span>
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 mt-1 ${isSelected ? 'text-blue-500' : 'text-gray-400'}`}/>
                </div>
              </div>
            )
          })}
        </div>
        {selected && (
          <div className="w-[520px] flex-shrink-0 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm sticky top-6" style={{ maxHeight: 'calc(100vh - 160px)' }}>
            <ContractDetailPanel contract={selected} onClose={() => setSelected(null)} canEdit={canEdit}/>
          </div>
        )}
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════
//  CM-01: PROJECT CONTRACTS components
// ══════════════════════════════════════════════════════════════════

function ProjectContractDetail({ con, onClose }: { con: typeof MOCK_PROJECT_CONTRACTS[0]; onClose: () => void }) {
  const [tab, setTab] = useState<'overview' | 'variations' | 'claims' | 'eot'>('overview')
  const conVars    = MOCK_VARIATIONS.filter(v => v.contractId === con.id)
  const conClaims  = MOCK_PROGRESS_CLAIMS.filter(c => c.contractId === con.id)
  const conEOTs    = MOCK_EOT_CLAIMS.filter(e => e.contractId === con.id)
  const urgentVars = conVars.filter(v => !v.noticeIssuedDate && v.status !== 'APPROVED' && ['URGENT','WARN'].includes(getNoticeUrgency(getVariationNoticeDaysLeft(v))))
  const approvedVarTotal = conVars.filter(v => v.status === 'APPROVED').reduce((s, v) => s + (v.approvedAmount ?? 0), 0)
  const runningSum = con.originalContractValue + approvedVarTotal
  const daysToPC   = Math.ceil((new Date(con.practicalCompletionDate).getTime() - Date.now()) / 86400000)
  const bondExpiring = con.bondExpiry && Math.ceil((new Date(con.bondExpiry).getTime() - Date.now()) / 86400000) < 90

  const TABS = [
    { key: 'overview',   label: 'Overview' },
    { key: 'variations', label: `Variations (${conVars.length})` },
    { key: 'claims',     label: `Claims (${conClaims.length})` },
    { key: 'eot',        label: `EOT (${conEOTs.length})` },
  ] as const

  return (
    <div className="flex flex-col" style={{ maxHeight: 'calc(100vh - 160px)' }}>
      {/* Header */}
      <div className="flex items-start justify-between px-5 py-4 border-b bg-gray-50">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs text-gray-400">{con.contractNumber}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${PROJ_TYPE_COLOR[con.contractType] ?? 'bg-gray-100 text-gray-600'}`}>
              {PROJ_TYPE_NAME[con.contractType] ?? con.contractType}
            </span>
          </div>
          <h2 className="text-sm font-bold text-gray-900">{con.jobNumber} — {con.clientName}</h2>
          <p className="text-xs text-gray-500 mt-0.5">Supt: {con.superintendentName} · {con.superintendentPhone}</p>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg flex-shrink-0 ml-3"><X className="w-4 h-4 text-gray-400"/></button>
      </div>

      {urgentVars.length > 0 && (
        <div className="mx-5 mt-3 bg-red-50 border border-red-200 rounded-xl px-3 py-2 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0"/>
          <p className="text-xs text-red-700 font-medium">{urgentVars.length} variation notice{urgentVars.length !== 1 ? 's' : ''} require action</p>
        </div>
      )}

      <div className="flex gap-0.5 bg-gray-100 rounded-lg m-4 mb-0 p-1">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key as any)}
            className={`text-xs px-2 py-1.5 rounded-md font-medium flex-1 transition-colors ${tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {tab === 'overview' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Original contract value', value: fmtAUD(con.originalContractValue), highlight: false },
                { label: 'Running sum (incl. vars)', value: fmtAUD(runningSum), highlight: true },
                { label: `Retention held (${con.retentionPercent}%)`, value: fmtAUD(con.retentionHeld), highlight: false },
                { label: 'Approved variations', value: fmtAUD(approvedVarTotal), highlight: false },
                { label: 'LD rate (per day)', value: fmtAUD(con.liquidatedDamagesRate), warn: con.liquidatedDamagesActive },
                { label: 'PC date', value: fmtDate(con.practicalCompletionDate), warn: daysToPC >= 0 && daysToPC <= 30 },
              ].map(({ label, value, highlight, warn }) => (
                <div key={label} className={`rounded-xl border px-3 py-2.5 ${highlight ? 'bg-[#1F3864] border-[#1F3864]' : warn ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100'}`}>
                  <div className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: highlight ? 'rgba(255,255,255,0.7)' : '#6b7280' }}>{label}</div>
                  <div className={`text-sm font-bold ${highlight ? 'text-white' : warn ? 'text-amber-700' : 'text-gray-900'}`}>{value}</div>
                </div>
              ))}
            </div>
            {con.bondRequired && (
              <div className={`rounded-xl border px-4 py-3 flex items-center gap-3 ${bondExpiring ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100'}`}>
                <Shield className={`w-4 h-4 flex-shrink-0 ${bondExpiring ? 'text-amber-600' : 'text-gray-400'}`}/>
                <div>
                  <p className="text-xs font-semibold text-gray-900">Performance bond — {fmtAUD(con.bondValue ?? 0)}</p>
                  <p className={`text-xs mt-0.5 ${bondExpiring ? 'text-amber-700 font-medium' : 'text-gray-500'}`}>
                    Expires: {fmtDate(con.bondExpiry)}{bondExpiring ? ' — renewal required' : ''}
                  </p>
                </div>
              </div>
            )}
            <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 text-xs text-gray-600 space-y-1">
              <div className="flex justify-between"><span>Defects liability period</span><span className="font-medium">{con.defectsLiabilityMonths} months from PC</span></div>
              <div className="flex justify-between"><span>DLP expiry (est.)</span>
                <span className="font-medium">{fmtDate(new Date(new Date(con.practicalCompletionDate).getTime() + con.defectsLiabilityMonths * 30.44 * 86400000).toISOString())}</span>
              </div>
              {con.contractType === 'NEC4' && <div className="pt-1 text-blue-600 italic">NEC4: early warning notices required within 8 weeks of identifying a compensation event.</div>}
              {con.notes && <div className="pt-1 italic text-gray-500">{con.notes}</div>}
            </div>
          </>
        )}

        {tab === 'variations' && (
          <div className="space-y-2">
            {conVars.length === 0 ? (
              <div className="text-center py-10 text-gray-400"><FileText className="w-8 h-8 mx-auto mb-2 text-gray-300"/><p className="text-sm">No variations on this contract</p></div>
            ) : conVars.map(vr => {
              const days    = getVariationNoticeDaysLeft(vr)
              const urgency = getNoticeUrgency(days)
              const sm      = { DRAFT:{bg:'bg-gray-100',text:'text-gray-600'}, SUBMITTED:{bg:'bg-blue-100',text:'text-blue-700'}, APPROVED:{bg:'bg-green-100',text:'text-green-700'}, REJECTED:{bg:'bg-red-100',text:'text-red-700'} }[vr.status as 'DRAFT'] ?? {bg:'bg-gray-100',text:'text-gray-600'}
              return (
                <div key={vr.id} className={`rounded-xl border p-3 ${(urgency==='URGENT'||urgency==='MISSED')&&!vr.noticeIssuedDate?'bg-red-50 border-red-200':urgency==='WARN'&&!vr.noticeIssuedDate?'bg-amber-50 border-amber-200':'bg-white border-gray-200'}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="text-xs font-mono text-gray-500">{vr.variationNumber}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{vr.status}</span>
                  </div>
                  <p className="text-xs font-semibold text-gray-900 mb-1">{vr.description}</p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Notice due: <span className={urgency==='URGENT'||urgency==='MISSED'?'text-red-600 font-semibold':urgency==='WARN'?'text-amber-600 font-semibold':''}>{fmtDate(vr.noticeDueDate)}</span>
                      {!vr.noticeIssuedDate && days !== null && (urgency==='URGENT'||urgency==='MISSED') && <span className="ml-1">({days! < 0 ? 'MISSED' : `${days}d`})</span>}
                    </span>
                    <span className="font-mono font-semibold text-gray-900">{fmtAUD(vr.totalExGST)}</span>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {tab === 'claims' && (
          <div className="space-y-2">
            {conClaims.length === 0 ? (
              <div className="text-center py-10 text-gray-400"><DollarSign className="w-8 h-8 mx-auto mb-2 text-gray-300"/><p className="text-sm">No progress claims on this contract</p></div>
            ) : conClaims.map(cl => {
              const sm = { DRAFT:{bg:'bg-gray-100',text:'text-gray-600'}, SUBMITTED:{bg:'bg-blue-100',text:'text-blue-700'}, CERTIFIED:{bg:'bg-amber-100',text:'text-amber-700'}, PAID:{bg:'bg-green-100',text:'text-green-700'} }[cl.status as 'DRAFT'] ?? {bg:'bg-gray-100',text:'text-gray-600'}
              return (
                <div key={cl.id} className="bg-white border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-gray-900">Claim #{cl.claimNumber} · {fmtDate(cl.claimPeriodFrom)} – {fmtDate(cl.claimPeriodTo)}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{cl.status}</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Net this claim: <strong className="text-gray-900">{fmtAUD(cl.thisClaimNet)}</strong></span>
                    <span>{cl.percentComplete}% complete</span>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {tab === 'eot' && (
          <div className="space-y-2">
            {conEOTs.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <Calendar className="w-8 h-8 mx-auto mb-2 text-gray-300"/>
                <p className="text-sm">No EOT claims on this contract</p>
                <p className="text-xs mt-1 text-gray-400">Log an extension of time claim from the Contracts page</p>
              </div>
            ) : conEOTs.map(eot => (
              <div key={eot.id} className="bg-white border border-gray-200 rounded-xl p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-mono text-gray-500">{eot.claimNumber}</span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">{eot.status}</span>
                </div>
                <p className="text-xs font-semibold text-gray-900 mb-1">{eot.delayEvent}</p>
                <div className="text-xs text-gray-500 space-y-0.5">
                  <div>Delay: {fmtDate(eot.delayEventDate)} · Notice: {fmtDate(eot.noticeIssuedDate)}</div>
                  <div>Days claimed: <strong>{eot.daysClaimedExtension}</strong></div>
                  <div>Evidence: {eot.evidenceTypes.join(', ')}</div>
                  {eot.notes && <div className="italic text-gray-400">{eot.notes}</div>}
                </div>
              </div>
            ))}
            <button className="btn-secondary text-xs flex items-center gap-1.5 w-full justify-center mt-2">
              <Plus className="w-3.5 h-3.5"/>New EOT claim
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function ProjectContractsTab({ canEdit }: { canEdit: boolean }) {
  const [selected, setSelected] = useState<typeof MOCK_PROJECT_CONTRACTS[0] | null>(null)

  // Merge seed contracts with any auto-created from Award-to-Activate
  const allContracts = [...MOCK_PROJECT_CONTRACTS, ...getCreatedContracts()]

  const totalOriginal  = allContracts.reduce((s, c) => s + c.originalContractValue, 0)
  const totalRetention = allContracts.reduce((s, c) => s + c.retentionHeld, 0)
  const urgentVarCount = MOCK_VARIATIONS.filter(v =>
    !v.noticeIssuedDate && v.status !== 'APPROVED' &&
    ['URGENT', 'WARN', 'MISSED'].includes(getNoticeUrgency(getVariationNoticeDaysLeft(v)))
  ).length

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Active project contracts', value: allContracts.length, color: 'text-[#1F3864]', sub: 'Across all active jobs' },
          { label: 'Total contracted value',   value: fmtAUD(totalOriginal),          color: 'text-gray-900',  sub: 'Original values' },
          { label: 'Retention held',           value: fmtAUD(totalRetention),         color: 'text-gray-700',  sub: 'Across all contracts' },
          { label: 'Variation notice alerts',  value: urgentVarCount,                 color: urgentVarCount > 0 ? 'text-red-600' : 'text-green-700', sub: urgentVarCount > 0 ? 'Require notice action' : 'All notices current' },
        ].map(({ label, value, color, sub }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className={`text-xl font-bold ${color}`}>{value}</div>
            <div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div>
            <div className="text-xs text-gray-400">{sub}</div>
          </div>
        ))}
      </div>

      {/* Contract register + detail */}
      {/* A13 CA Agent sidebar */}
      <AgentSidebar context={{
        page: 'contracts',
        contracts:  MOCK_PROJECT_CONTRACTS,
        variations: MOCK_VARIATIONS,
        claims:     MOCK_PROGRESS_CLAIMS,
        platformDate: '2026-06-17',
      }} />

      {/* Contract register + detail */}
      <div className="flex gap-4 items-start">
        <div className="flex-1 min-w-0 space-y-2">
          {allContracts.map(con => {
            const isSelected = selected?.id === con.id
            const vars = MOCK_VARIATIONS.filter(v => v.contractId === con.id)
            const urgentVars = vars.filter(v => !v.noticeIssuedDate && v.status !== 'APPROVED' && ['URGENT','WARN','MISSED'].includes(getNoticeUrgency(getVariationNoticeDaysLeft(v))))
            const approvedVarTotal = vars.filter(v => v.status === 'APPROVED').reduce((s, v) => s + (v.approvedAmount ?? 0), 0)
            const runningSum = con.originalContractValue + approvedVarTotal
            const daysToPC   = con.practicalCompletionDate ? Math.ceil((new Date(con.practicalCompletionDate).getTime() - Date.now()) / 86400000) : null
            const bondExpiring = con.bondExpiry && Math.ceil((new Date(con.bondExpiry).getTime() - Date.now()) / 86400000) < 90
            const isAutoCreated = !!(con as any)._fromTenderReference
            return (
              <div key={con.id} onClick={() => setSelected(isSelected ? null : con)}
                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${
                  isSelected ? 'border-blue-400 ring-2 ring-blue-100 shadow-sm' :
                  urgentVars.length > 0 ? 'border-red-200 bg-red-50/20' :
                  bondExpiring ? 'border-amber-200' : 'border-gray-200 hover:border-gray-300'
                }`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono text-xs text-gray-400">{con.contractNumber}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${PROJ_TYPE_COLOR[con.contractType] ?? 'bg-gray-100 text-gray-600'}`}>
                        {PROJ_TYPE_NAME[con.contractType] ?? con.contractType}
                      </span>
                      {isAutoCreated && (
                        <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5">
                          ✓ From tender {(con as any)._fromTenderReference}
                        </span>
                      )}
                      {urgentVars.length > 0 && (
                        <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5">
                          <AlertTriangle className="w-3 h-3"/>{urgentVars.length} notice alert{urgentVars.length !== 1 ? 's' : ''}
                        </span>
                      )}
                      {bondExpiring && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">⚠ Bond expiring</span>}
                    </div>
                    <div className="font-semibold text-gray-900 text-sm">{con.jobNumber} — {con.clientName}</div>
                    {isAutoCreated && (
                      <p className="text-xs text-amber-700 mt-0.5">⚠ LD rate, bond and superintendent not yet populated — complete before site commencement.</p>
                    )}
                    <div className="flex items-center gap-3 mt-2 flex-wrap text-xs text-gray-400">
                      <span className="flex items-center gap-1"><DollarSign className="w-3 h-3"/>Running sum: <strong className="text-gray-700 ml-0.5">{fmtAUD(runningSum)}</strong></span>
                      {con.practicalCompletionDate && (
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3"/>PC: {fmtDate(con.practicalCompletionDate)}
                          {daysToPC !== null && daysToPC >= 0 && daysToPC <= 60 && <span className={`ml-1 font-medium ${daysToPC <= 14 ? 'text-red-600' : daysToPC <= 30 ? 'text-amber-600' : 'text-gray-600'}`}>({daysToPC}d)</span>}
                        </span>
                      )}
                      <span>LD: {fmtAUD(con.liquidatedDamagesRate)}/day</span>
                      <span>Ret: {con.retentionPercent}%</span>
                      {vars.length > 0 && <span>{vars.length} variation{vars.length !== 1 ? 's' : ''}</span>}
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 mt-1 ${isSelected ? 'text-blue-500' : 'text-gray-400'}`}/>
                </div>
              </div>
            )
          })}
        </div>
        {selected && (
          <div className="w-[480px] flex-shrink-0 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm sticky top-6">
            <ProjectContractDetail con={selected} onClose={() => setSelected(null)}/>
          </div>
        )}
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════
//  MAIN EXPORT — two-tab page
// ══════════════════════════════════════════════════════════════════

export function Contracts() {
  const { isTier } = useAuthStore()
  const isPM    = isTier('B_PM')
  const isAdmin = isTier('A_DEVELOPER')
  const canEdit = isPM || isAdmin

  const [pageTab, setPageTab] = useState<'project' | 'supplier'>('project')

  // Badge: count of variation notice alerts for the tab indicator
  const varAlerts = MOCK_VARIATIONS.filter(v =>
    !v.noticeIssuedDate && v.status !== 'APPROVED' &&
    ['URGENT', 'WARN', 'MISSED'].includes(getNoticeUrgency(getVariationNoticeDaysLeft(v)))
  ).length

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#2E75B6]"/>Contract Management
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Project contracts · supplier compliance · variation notices · bond and PC date tracking
          </p>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        <button
          onClick={() => setPageTab('project')}
          className={`text-sm font-medium px-5 py-2 rounded-lg flex-1 transition-colors flex items-center justify-center gap-2 ${pageTab === 'project' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          📋 Project Contracts
          {varAlerts > 0 && <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold">{varAlerts}</span>}
        </button>
        <button
          onClick={() => setPageTab('supplier')}
          className={`text-sm font-medium px-5 py-2 rounded-lg flex-1 transition-colors ${pageTab === 'supplier' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          🛡 Supplier Compliance ({MOCK_SUPPLIER_CONTRACTS.length})
        </button>
      </div>

      {pageTab === 'project'  && <ProjectContractsTab canEdit={canEdit}/>}
      {pageTab === 'supplier' && <SupplierComplianceTab canEdit={canEdit}/>}
    </div>
  )
}
